// ABOUTME: Modal listing the result of a bulk label print per order, with a button per row
// ABOUTME: that prints that order's labels again on the same workspace printer.

import { Box, CircularProgress, IconButton, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import PrintIcon from '@mui/icons-material/Print';
import SHQuickAction from "../../theme/Modal/SHQuickAction";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import ShLabel from "../../theme/label/ShLabel";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ShTextSkeleton from "../../theme/skeleton/ShTextSkeleton";
import GatedShDataGrid2 from "../../theme/datagrid2/GatedShDataGrid2";
import { TabContextProvider } from "../../../contexts/TabContext";
import { GridFilterProvider } from "../../../contexts/GridFilterContext";
import { tokens } from "../../../theme";
import { usePrintOrderLabelsMutation } from "../../../newapi/order/ordersSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../utils/toasts";

const gridOptions = {
    export: false,
    disableCustomActionsOnPhone: false,
    isSidepanel: false,
    hide: true,
};

// The grid needs a non-empty config before it asks for rows
const gridConfig = { printResults: true };

const sortValue = (row, field) => {
    const value = field === "trackingNumbers" ? (row.trackingNumbers ?? []).join(", ") : row[field];
    return String(value ?? "").toLowerCase();
};

const MPrintedOrderLabels = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const [rows, setRows] = useState(props.results ?? []);
    const [reprintingId, setReprintingId] = useState(null);
    const [printOrderLabels] = usePrintOrderLabelsMutation();

    useEffect(() => {
        setRows(props.results ?? []);
    }, [props.results]);

    // ShDataGrid2 reads its rows through a query hook. This serves the print results kept here in
    // that shape, all of them at once, sorted as the grid asks. Every answer is kept per sort, so
    // the grid gets the same data object back on each render and does not reload in a loop.
    const answers = useMemo(() => ({ forRows: rows, bySort: new Map() }), [rows]);
    const printResultsContent = useCallback(({ args }) => {
        const sort = args?.body?.sort;
        const key = `${sort?.field ?? ""}|${sort?.type ?? ""}`;
        if (!answers.bySort.has(key)) {
            const sorted = sort?.field
                ? [...rows].sort((a, b) => sortValue(a, sort.field).localeCompare(sortValue(b, sort.field)) * (String(sort.type).toLowerCase() === "desc" ? -1 : 1))
                : rows;
            answers.bySort.set(key, { data: sorted, totalItems: sorted.length, totalPages: 1 });
        }
        return {
            data: answers.bySort.get(key),
            isLoading: false,
            isFetching: false,
            isSuccess: true,
            isError: false,
            error: null,
            refetch: () => {}
        };
    }, [rows, answers]);

    const printedCount = rows.filter(row => row.success).length;
    const failedCount = rows.length - printedCount;

    const handlePrintAgain = async (row) => {
        if (reprintingId !== null) return;
        setReprintingId(row.id);
        try {
            const response = await printOrderLabels({
                orderIds: [row.orderId],
                workspaceId: props.workspaceId
            }).unwrap();

            const result = response.results?.[0];
            if (result) {
                setRows(prev => prev.map(x => x.id === result.id ? result : x));
            }
            showToastMessage(result?.success ? "success" : "error", t(result?.success ? "label_sent_to_printer" : "label_print_failed"));
        } catch (err) {
            showToastMessageRequestError(t("label_print_failed"), t, err);
        } finally {
            setReprintingId(null);
        }
    };

    const columns = [
        { field: "barcode", headerName: t("order"), flex: 1,
            renderCell: (params) => params.row.skeleton ? <ShTextSkeleton/> : <Text>{params.row.barcode}</Text>
        },
        { field: "webshopreference", headerName: t("webshopreference"), flex: 1,
            renderCell: (params) => params.row.skeleton ? <ShTextSkeleton/> : <Text>{params.row.webshopreference}</Text>
        },
        { field: "deliveryName", headerName: t("delivery_name"), flex: 1,
            renderCell: (params) => params.row.skeleton ? <ShTextSkeleton/> : <Text>{params.row.deliveryName}</Text>
        },
        {
            field: "trackingNumbers",
            headerName: t("tracking_number"),
            flex: 1.5,
            renderCell: (params) => params.row.skeleton ? <ShTextSkeleton/> : <Text>{(params.row.trackingNumbers ?? []).join(", ")}</Text>
        },
        {
            field: "success",
            headerName: t("status"),
            flex: 1.5,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>;
                }
                const label = (
                    <ShLabel size={32} fitted palette="normal" variant={params.row.success ? "green" : "red"}>
                        {t(params.row.success ? "printed" : "failed")}
                    </ShLabel>
                );

                if (params.row.success || !params.row.error) {
                    return label;
                }

                // The reason a print failed shows when hovering the failed state
                return (
                    <SHQuickAction
                        disableScrollLock={true}
                        component={
                            <Box sx={{ px: 1.5, display: "flex", flexDirection: "column" }}>
                                <Text light>{t("reason")}</Text>
                                <Text>{params.row.error}</Text>
                            </Box>
                        }
                    >
                        {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                            <Box
                                aria-owns={anchorEl ? 'simple-popover' : undefined}
                                aria-haspopup={false}
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{ display: "flex", alignItems: "center" }}
                            >
                                {label}
                            </Box>
                        )}
                    </SHQuickAction>
                );
            }
        },
        {
            field: "actions",
            headerName: "",
            width: 72,
            sortable: false,
            renderCell: (params) => (
                params.row.skeleton ? null : reprintingId === params.row.id
                    ? <CircularProgress size={20} />
                    : <IconButton
                        type="button"
                        aria-label={t("print_again")}
                        title={t("print_again")}
                        disabled={reprintingId !== null}
                        onClick={(e) => { e.stopPropagation(); handlePrintAgain(params.row); }}
                        sx={{
                            color: colors.txt["primary"]
                        }}
                    >
                        <PrintIcon />
                    </IconButton>
            )
        }
    ];

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
            sx={{ body: { minWidth: 900 } }}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Subheading>{t("printed_labels")}</Subheading>
                <Text light>{t("printed_labels_summary", { printed: printedCount, failed: failedCount })}</Text>
            </Box>
            {/* Every result in one list, scrolling within the modal instead of paging */}
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    display: "flex",
                    flexDirection: "column",
                    overflow: "auto",
                    maxHeight: "60vh"
                }}
            >
                <Box height={1} display={"flex"} flexDirection={"column"}>
                    <TabContextProvider>
                        <GridFilterProvider activeChild={"printed_order_labels"}>
                            <GatedShDataGrid2 key={theme.palette.mode}
                                content={printResultsContent}
                                config={gridConfig}
                                disablePagination
                                gridOptions={gridOptions}
                                columns={columns}
                                customPaddingBottom={1}
                            />
                        </GridFilterProvider>
                    </TabContextProvider>
                </Box>
            </Box>
            <Box
                component="footer"
                sx={{
                    width: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingX: 3,
                    paddingY: 2,
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>{t("close")}</Shbutton>
            </Box>
        </SHModal>
    );
};

export default MPrintedOrderLabels;
